import type { FleetProjectSummary } from "../types.ts";

/** Resolve origin session for a project (legacy rows may omit the field). */
export function resolveProjectOriginSessionKey(project: FleetProjectSummary): string {
  const origin = project.originSessionKey?.trim();
  if (origin) {
    return origin;
  }
  return project.sessionKey.trim();
}

/** Parent chat session when viewing a project fork (or the session itself). */
export function resolveFleetProjectsContextSessionKey(
  sessionKey: string,
  projects: FleetProjectSummary[],
): string {
  const key = sessionKey.trim();
  if (!key) {
    return key;
  }
  const asProject = projects.find((p) => p.sessionKey.trim() === key);
  if (asProject) {
    return resolveProjectOriginSessionKey(asProject);
  }
  return key;
}

/** Projects created from the active chat (including when the current tab is a project fork). */
export function projectsForChatContext(
  projects: FleetProjectSummary[],
  sessionKey: string,
): FleetProjectSummary[] {
  const contextKey = resolveFleetProjectsContextSessionKey(sessionKey, projects);
  return projectsForActiveSession(projects, contextKey);
}

/** Projects created from or attributed to the active chat session. */
export function projectsForActiveSession(
  projects: FleetProjectSummary[],
  sessionKey: string,
): FleetProjectSummary[] {
  const key = sessionKey.trim();
  if (!key) {
    return [];
  }
  return projects.filter((p) => resolveProjectOriginSessionKey(p) === key);
}

export function findChatContextProject(
  projects: FleetProjectSummary[],
  sessionKey: string,
  projectId: string,
): FleetProjectSummary | null {
  const id = projectId.trim();
  if (!id) {
    return null;
  }
  return projectsForChatContext(projects, sessionKey).find((p) => p.id === id) ?? null;
}

export function pruneChatSendProjectId(
  sessionKey: string,
  projectId: string | null,
  projects: FleetProjectSummary[],
): string | null {
  const id = projectId?.trim() ?? "";
  if (!id) {
    return null;
  }
  return findChatContextProject(projects, sessionKey, id) ? id : null;
}

export type ResolveSelfImprovementProjectIdResult =
  | { ok: true; projectId: string | undefined }
  | { ok: false; error: string };

/**
 * Resolve fleet project id for self-improvement.start.
 * Explicit send selection must be valid; stale chatActiveProjectId is omitted.
 */
export function resolveSelfImprovementProjectId(params: {
  projects: FleetProjectSummary[];
  sessionKey: string;
  chatSendProjectId: string | null | undefined;
  chatActiveProjectId: string | null | undefined;
}): ResolveSelfImprovementProjectIdResult {
  const sendId = params.chatSendProjectId?.trim() ?? "";
  if (sendId) {
    const found = findChatContextProject(params.projects, params.sessionKey, sendId);
    if (!found) {
      return {
        ok: false,
        error: "Selected project is no longer available for this chat.",
      };
    }
    return { ok: true, projectId: found.id };
  }

  const activeId = params.chatActiveProjectId?.trim() ?? "";
  if (activeId) {
    const found = findChatContextProject(params.projects, params.sessionKey, activeId);
    if (found) {
      return { ok: true, projectId: found.id };
    }
    return { ok: true, projectId: undefined };
  }

  return { ok: true, projectId: undefined };
}
